import React from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Bug, Link2, FileWarning, Smartphone, Gauge, Accessibility,
  Crosshair, ScanSearch, FileCheck2, Sparkles, Search, ShieldCheck,
  Image, Globe, ArrowRight, Check, X,
} from "lucide-react";
import Footer from "../components/Footer";
import PipelineCanvas from "../components/PipelineCanvas";
import { PARENT_URL } from "../utils/branding";

export default function BugScoutLanding() {
  const nav = useNavigate();

  return (
    <div className="au-root">
      {/* NAV */}
      <nav className="au-nav" aria-label="Primary">
        <Link to="/" className="au-brand">
          <span className="au-mark"><Bug /></span>
          BUGSCOUT
        </Link>
        <div className="au-navlinks">
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <Link to="/about">About</Link>
          <a href={PARENT_URL}>GetKhojo</a>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="au-pill au-pill-ghost au-pill-sm" onClick={() => nav("/login")}>Login</button>
          <button className="au-pill au-pill-primary au-pill-sm" onClick={() => nav("/register")}>
            Start Free <ArrowRight />
          </button>
        </div>
      </nav>

      {/* HERO — editorial two-column */}
      <header className="au-container au-section" style={{ paddingTop: 72 }}>
        <div className="au-marker" style={{ display: "flex", justifyContent: "space-between", marginBottom: 28 }}>
          <span>/ AUTOMATED WEB TESTING</span>
          <span>·· 00 / 03</span>
        </div>
        <div className="au-rule" />

        <div
          className="au-grid"
          style={{ gridTemplateColumns: "1.35fr 1fr", alignItems: "end", marginTop: 40 }}
        >
          <div>
            <span className="au-eyebrow">Khojo BugScout</span>
            <h1 style={{ fontSize: "clamp(3rem, 8vw, 6.5rem)", marginTop: 18 }}>
              We catch<br />
              what others<br />
              <span className="au-em">miss.</span>
            </h1>
          </div>

          <div style={{ paddingBottom: 8 }}>
            <p className="au-lead" style={{ maxWidth: 420 }}>
              Comprehensive automated testing for modern web applications. Detect broken
              links, JavaScript errors, mobile issues, performance bottlenecks — all in
              one scan.
            </p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 28 }}>
              <button className="au-pill au-pill-primary" onClick={() => nav("/register")}>
                Start Free Test <ArrowRight />
              </button>
              <button
                className="au-pill au-pill-glass"
                onClick={() =>
                  document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })
                }
              >
                See Features
              </button>
            </div>
          </div>
        </div>

        {/* Stat row — editorial figures */}
        <div className="au-rule" style={{ marginTop: 56 }} />
        <div className="au-grid au-cols-4" style={{ gap: 0 }}>
          {[
            ["10", "+", "Bug Categories"],
            ["<60", "s", "Avg Scan Time"],
            ["100", "%", "Automated"],
            ["24/7", "", "Available"],
          ].map(([n, unit, l], i) => (
            <div key={i} style={{ padding: "26px 24px 26px 0" }}>
              <div className="au-stat-num">
                {n}
                {unit && <span className="au-unit">{unit}</span>}
              </div>
              <div className="au-stat-lbl">{l}</div>
            </div>
          ))}
        </div>
      </header>

      {/* SIGNATURE — test-pipeline node canvas */}
      <section className="au-container au-section">
        <div className="au-marker" style={{ display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
          <span>/ PIPELINE</span>
          <span>·· 01 / 03</span>
        </div>
        <h2 style={{ fontSize: "clamp(2rem, 5vw, 3.6rem)", marginBottom: 28 }}>
          From crawl to <span className="au-em">report.</span>
        </h2>

        <PipelineCanvas
          ariaLabel="BugScout test pipeline: Crawl and Test feed Findings, which produces Report and Ship"
          nodes={[
            { id: "crawl",    x: 17, y: 23, icon: <Crosshair />,  title: "Crawl",    sub: "Discover every page & link" },
            { id: "test",     x: 17, y: 77, icon: <ScanSearch />, title: "Test",     sub: "10+ checks in parallel" },
            { id: "findings", x: 50, y: 50, icon: <Bug />,        title: "Findings", sub: "Severity-ranked issues" },
            { id: "report",   x: 83, y: 23, icon: <FileCheck2 />, title: "Report",   sub: "Locations & fix hints" },
            { id: "ship",     x: 83, y: 77, icon: <Sparkles />,   title: "Ship",     sub: "Bug-free, with confidence" },
          ]}
          edges={[
            { from: "crawl", to: "findings" },
            { from: "test",  to: "findings" },
            { from: "findings", to: "report" },
            { from: "findings", to: "ship", dim: true },
          ]}
        />
      </section>

      {/* HOW IT WORKS — editorial numbered list */}
      <section className="au-container au-section">
        <div className="au-marker" style={{ display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
          <span>/ HOW IT WORKS</span>
          <span>·· 02 / 03</span>
        </div>
        <h2 style={{ fontSize: "clamp(2rem, 5vw, 3.6rem)", marginBottom: 8 }}>
          3 steps. <span className="au-em">Zero setup.</span>
        </h2>
        <div className="au-rule" style={{ marginTop: 28 }} />

        <div className="au-grid au-cols-3">
          {[
            { n: "01", icon: <Search />, t: "Enter URL", d: "Just paste your website URL. No credit card, no installation, no configuration needed." },
            { n: "02", icon: <ScanSearch />, t: "We Scan", d: "Our automated engine performs 10+ types of tests in parallel using headless browser technology." },
            { n: "03", icon: <FileCheck2 />, t: "Get Report", d: "Detailed report with severity levels, locations of issues, and fix suggestions for each bug found." },
          ].map((s, i) => (
            <article key={i} style={{ padding: "8px 24px 8px 0" }}>
              <div
                className="au-marker"
                style={{ fontSize: "2.6rem", lineHeight: 1, opacity: 0.85, fontWeight: 400 }}
              >
                {s.n}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0 10px" }}>
                <span className="au-badge"><span aria-hidden>{s.icon}</span></span>
                <h3 style={{ fontSize: "1.5rem" }}>{s.t}</h3>
              </div>
              <p style={{ color: "var(--au-text-2)", lineHeight: 1.7, fontSize: "0.95rem" }}>{s.d}</p>
            </article>
          ))}
        </div>
      </section>

      {/* FEATURES — what we detect */}
      <section id="features" className="au-container au-section">
        <div className="au-marker" style={{ display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
          <span>/ WHAT WE DETECT</span>
          <span>·· 03 / 03</span>
        </div>
        <h2 style={{ fontSize: "clamp(2rem, 5vw, 3.6rem)", marginBottom: 8 }}>
          10+ checks. <span className="au-em">One scan.</span>
        </h2>
        <div className="au-rule" style={{ marginTop: 28, marginBottom: 32 }} />

        <div className="au-grid au-cols-3">
          {[
            { icon: <Link2 />, t: "Broken Links", d: "Detect dead URLs, 404s, redirects gone wrong, and unreachable resources across your site." },
            { icon: <FileWarning />, t: "JavaScript Errors", d: "Catch console errors, runtime exceptions, and uncaught promises that break user experience." },
            { icon: <Smartphone />, t: "Mobile Issues", d: "Find horizontal scroll, missing viewport tags, touch target problems, and responsive failures." },
            { icon: <Gauge />, t: "Performance", d: "Slow page loads, render-blocking resources, oversized assets, and bottleneck identification." },
            { icon: <Search />, t: "SEO Problems", d: "Missing meta tags, duplicate titles, broken canonicals, missing alt texts, sitemap issues." },
            { icon: <ShieldCheck />, t: "Security Headers", d: "Check for missing CSP, X-Frame-Options, HSTS, and other critical security headers." },
            { icon: <Image />, t: "Image Issues", d: "Broken images, missing alt attributes, oversized files, and accessibility violations." },
            { icon: <Globe />, t: "Network Errors", d: "4xx and 5xx HTTP errors, failed API calls, CORS issues, and network failures." },
            { icon: <Accessibility />, t: "Accessibility", d: "WCAG compliance issues, contrast problems, keyboard navigation, ARIA failures." },
          ].map((f, i) => (
            <article key={i} className="au-glass au-pad au-lift">
              <span className="au-badge"><span aria-hidden>{f.icon}</span></span>
              <h3 style={{ fontSize: "1.25rem", margin: "16px 0 8px" }}>{f.t}</h3>
              <p style={{ color: "var(--au-text-2)", lineHeight: 1.6, fontSize: "0.9rem" }}>{f.d}</p>
            </article>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="au-container au-section">
        <div className="au-marker" style={{ marginBottom: 18 }}>/ SIMPLE PRICING</div>
        <h2 style={{ fontSize: "clamp(2rem, 5vw, 3.6rem)", marginBottom: 8 }}>
          Start free. <span className="au-em">Scale when ready.</span>
        </h2>
        <div className="au-rule" style={{ marginTop: 28, marginBottom: 32 }} />

        <div className="au-grid au-cols-3">
          <PriceCard
            name="STARTER" price="₹0" period="forever"
            features={[
              [true, "2 tests per day"],
              [true, "Basic bug detection"],
              [true, "Critical & High issues"],
              [true, "Email reports"],
              [false, "Performance metrics"],
              [false, "SEO deep analysis"],
              [false, "API access"],
            ]}
            cta="Start Free" ctaAction={() => nav("/register")} highlight={false}
          />
          <PriceCard
            name="PROFESSIONAL" price="₹2,499" period="per month"
            features={[
              [true, "50 tests per day"],
              [true, "All bug categories"],
              [true, "Performance metrics"],
              [true, "SEO deep analysis"],
              [true, "Mobile responsive"],
              [true, "PDF/CSV exports"],
              [true, "Priority support"],
            ]}
            cta="Upgrade Now" ctaAction={() => nav("/register")} highlight badge="POPULAR"
          />
          <PriceCard
            name="ENTERPRISE" price="Custom" period="contact us"
            features={[
              [true, "Unlimited tests"],
              [true, "Custom integrations"],
              [true, "White-label reports"],
              [true, "API access"],
              [true, "Dedicated support"],
              [true, "Custom SLA"],
              [true, "On-premise option"],
            ]}
            cta="Contact Sales"
            ctaAction={() => (window.location.href = "mailto:support@getkhojo.com")}
            highlight={false}
          />
        </div>
      </section>

      {/* CTA */}
      <section className="au-container au-section au-center">
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <span className="au-eyebrow">Find · Fix · Ship</span>
          <h2 style={{ fontSize: "clamp(2.2rem, 6vw, 4.2rem)", margin: "18px 0 16px" }}>
            Ready to ship<br />
            <span className="au-em">without bugs?</span>
          </h2>
          <p className="au-lead" style={{ marginBottom: 32 }}>
            Start with our free tier. No credit card required.
          </p>
          <button className="au-pill au-pill-primary" onClick={() => nav("/register")}>
            Start Free Test <ArrowRight />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function PipeNode({ icon, title, sub, style }) {
  return (
    <div className="au-node au-np" style={style}>
      <span className="au-node-ico" aria-hidden>{icon}</span>
      <div>
        <div className="au-node-title">{title}</div>
        <div className="au-node-sub">{sub}</div>
      </div>
      <Check className="au-check" aria-hidden />
    </div>
  );
}

function PriceCard({ name, price, period, features, cta, ctaAction, highlight, badge }) {
  return (
    <div
      className="au-glass au-pad-lg"
      style={{
        position: "relative",
        borderColor: highlight ? "var(--au-accent)" : undefined,
        boxShadow: highlight ? "var(--au-shadow-float), var(--au-glow)" : undefined,
      }}
    >
      {badge && (
        <span
          className="au-chip au-chip-cat au-cat-green"
          style={{ position: "absolute", top: -12, right: 24 }}
        >
          {badge}
        </span>
      )}
      <div className="au-marker" style={{ marginBottom: 14 }}>{name}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span className="au-stat-num" style={{ fontSize: "2.4rem" }}>{price}</span>
        <span style={{ fontSize: "0.8rem", color: "var(--au-muted)" }}>{period}</span>
      </div>
      <div className="au-rule" style={{ margin: "20px 0" }} />
      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", minHeight: 220, display: "flex", flexDirection: "column", gap: 11 }}>
        {features.map(([on, label], i) => (
          <li
            key={i}
            style={{
              display: "flex", alignItems: "center", gap: 10, fontSize: "0.9rem",
              color: on ? "var(--au-text)" : "var(--au-muted)",
            }}
          >
            {on ? (
              <Check size={16} style={{ color: "var(--au-accent-ink)", flex: "none" }} aria-hidden />
            ) : (
              <X size={16} style={{ color: "var(--au-muted)", flex: "none" }} aria-hidden />
            )}
            <span>{label}</span>
          </li>
        ))}
      </ul>
      <button
        className={`au-pill ${highlight ? "au-pill-primary" : "au-pill-glass"}`}
        style={{ width: "100%" }}
        onClick={ctaAction}
      >
        {cta}
      </button>
    </div>
  );
}
